import Select, {components, MultiValue} from "react-select";
import React from "react";
import CheckBox from "react-three-state-checkbox";
import type { CombinedHSFacility } from "../../../../syncstream/utils/FacilityUtils";

interface ISelectWards {
    wards: CombinedHSFacility[],
    wingLabel: string,
    selectedWardIds?: string[],
    selectedWardNames?: string[],
    selectionChanged: (wards: string[]) => void
}

//
// There can be multiple facilities (i.e. wards) that share the same MS Ward Name but have to use different HS Facility Names
// so can be supplied by different pharmacies, even though they are essentially the one physical ward. We only want to display
// one checkbox item per MS Ward Name in this SelectWard checkbox list so just take the first hsId for the value then the
// contributingFacilityIds can be used to set all the selected hsId's to control visibility of patients on dashboard when
// the wards checkboxes selections are changed.
//
interface IFacilityOption {
    label: string;
    value: number; // This should be the first hsId in the contributingFacilityIds and can be used to ensure only show one item per shared MS Ward Name
    contributingFacilityIds: number[];
}

export function SelectWards(props: ISelectWards) {
    const { wards, wingLabel, selectedWardIds, selectionChanged } = props;
    const facilityOptions = wards.map((f) => ({ label: f.msWardNameOrName!, value: f.contributingHsIds[0]!, contributingFacilityIds: f.contributingHsIds! }));
    const allOptions = [
        {
            label: "All " + wingLabel,
            options: facilityOptions,
            value: -1
        }
    ];

    const selectedFacilityOptions = facilityOptions.filter((x) => !selectedWardIds || selectedWardIds.includes(x.value.toString()));

    // We need explicit control of the ward menu being open or closed so that the "select all" button
    // can close the ward menu list.
    const checkboxState: "All" | "Some" | "None" = selectedFacilityOptions.length === 0 ? "None" : selectedFacilityOptions.length === facilityOptions.length ? "All" : "Some";
    function handleWardSelectionChange(newValue: MultiValue<IFacilityOption>) {
        const selectedOptions = newValue.map((option) => option);

        const selectedFacilityIds = selectedOptions.flatMap((option) =>
            option.contributingFacilityIds.map((id) => id.toString())
        );

        selectionChanged(selectedFacilityIds);
    }

    function handleSelectAll() {
        // setSelectedFacilityOptions([...facilityOptions]);
        if (checkboxState === "All") {
            // Clear.
            selectionChanged([]);
        }
        else {
            // Select all.
            const allFacilityIds = facilityOptions.flatMap((option) =>
                option.contributingFacilityIds.map((id: number) => id.toString())
            )
            selectionChanged(allFacilityIds);
        }
    }

    return <Select
        aria-label="Select Wing"
        isMulti
        placeholder={`Select ${wingLabel}`}
        options={allOptions}
        value={selectedFacilityOptions}
        closeMenuOnSelect={false}
        onChange={handleWardSelectionChange}
        styles={{
            control: (provided) => ({ ...provided, height: '100%', minWidth: '300px', zIndex: 9999}),
            menu: provided =>({...provided, zIndex: 9999})
        }}

        components={{
            MultiValue: ({ index, getValue }) => {
                const values = getValue();
                let valueLabel = '';

                if (values.length > 0) {
                    valueLabel += values[0].label;
                }
                if (values.length > 1) {
                    valueLabel += ` & ${values.length - 1} more`;
                }
                if (values.length === facilityOptions.length) {
                    valueLabel = `All ${wingLabel}s`;
                }

                return <>{!index && valueLabel}</>; // only show one entry
            },
            Option: (props) => {
                return (
                    <div>
                        <components.Option {...props}>
                            <input type="checkbox" checked={props.isSelected} onChange={() => null} />{' '}
                            <label>{props.label}</label>
                        </components.Option>
                    </div>
                );
            },
            GroupHeading: () => {
                return (
                    <div>
                        <CheckBox checked={checkboxState === "All"} indeterminate={checkboxState === "Some" } onChange={handleSelectAll} />{' '}
                        <label>Wards</label>
                    </div>
                );
            }
        }}
        hideSelectedOptions={false}
    />
}

